import { logError } from '../lib/logger';
import { emit as debug } from '../lib/debugBus';
import { setMediaModalOpen } from '../lib/mediaModal';
import { tryAutoCompleteVideoOnClose, tryAutoCompleteVideoOnEnded } from '../lib/progressAutoComplete';
import { resolveVideoPlayback } from '../lib/videoEmbed';
import { downloadVideoContent } from '../lib/videoDownload';
import { useAuth } from '../contexts/AuthContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Download, Loader2, X } from 'lucide-react';

// Stili per impedire selezione / long-press menu nativo iOS sul video.
// `WebkitTouchCallout: 'none'` disattiva la callout "Salva immagine/video"
// che iOS Safari mostra al long-press sui media.
const NO_INTERACTION_STYLE = {
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
  WebkitUserDrag: 'none',
};

const swallowEvent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IFRAME_LOAD_TIMEOUT_MS = 12000;

const VideoModal = ({ content, onClose, engagementSource = 'main' }) => {
  const { user } = useAuth();
  const openedAtRef = useRef(null);
  const [mediaReady, setMediaReady] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);

  const watermarkLabel = (user?.email || user?.name || '').trim();
  const allowDownload = Boolean(content?.allow_video_download);

  const { embedUrl, sourceUrl, isDirectVideo } = useMemo(
    () => resolveVideoPlayback(content?.video_url, { autoplay: true }),
    [content?.video_url]
  );

  useEffect(() => {
    if (!content?.id) return undefined;
    debug('modal:mount', {
      id: content.id,
      mode: isDirectVideo ? 'video' : embedUrl ? 'iframe' : 'none',
      src: isDirectVideo ? sourceUrl : embedUrl,
    });
    openedAtRef.current = Date.now();
    setMediaModalOpen(true);
    document.body.style.overflow = 'hidden';

    axios
      .post(`${API}/progress/view`, { content_id: content.id, source: engagementSource })
      .catch((e) => logError('VideoModal:view', e));

    return () => {
      debug('modal:unmount', { id: content.id });
      setMediaModalOpen(false);
      document.body.style.overflow = '';
      openedAtRef.current = null;
    };
  }, [content?.id, engagementSource, embedUrl, sourceUrl, isDirectVideo]);

  useEffect(() => {
    setMediaReady(false);
    setLoadTimedOut(false);
    setMediaError(false);
  }, [content?.id, reloadNonce]);

  useEffect(() => {
    if (mediaReady) return undefined;
    const t = setTimeout(() => {
      debug('modal:loadTimeout', { id: content?.id || null });
      setLoadTimedOut(true);
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [mediaReady, content?.id, reloadNonce]);

  const handleClose = useCallback(() => {
    debug('modal:closeBtn', { id: content?.id || null });
    tryAutoCompleteVideoOnClose(content?.id, openedAtRef.current);
    onClose();
  }, [content?.id, onClose]);

  const handleRetry = useCallback(() => {
    debug('modal:retry', { id: content?.id || null });
    setMediaError(false);
    setReloadNonce((n) => n + 1);
  }, [content?.id]);

  const handleDownload = useCallback(async () => {
    if (downloading || !allowDownload) return;
    setDownloading(true);
    setDownloadProgress(null);
    try {
      const result = await downloadVideoContent(content, {
        engagementSource,
        onProgress: (ratio) => setDownloadProgress(ratio),
      });
      if (result?.mode === 'opened_external' && result.message) {
        alert(result.message);
      }
    } catch (err) {
      alert('Impossibile scaricare il video. Riprova.');
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  }, [content, downloading, allowDownload, engagementSource]);

  if (!content) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-3 md:p-8 bg-black"
      data-testid="video-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-modal-title"
    >
      <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between gap-2 p-4 border-b border-zinc-200">
          <div className="min-w-0">
            <h2
              id="video-modal-title"
              className="text-lg font-medium text-zinc-900 truncate"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {content.title}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {allowDownload && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] disabled:opacity-60 text-white text-sm font-medium transition-colors"
                data-testid="video-download-btn"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                <span className="text-sm">
                  {downloading
                    ? (downloadProgress != null ? `${Math.round(downloadProgress * 100)}%` : '...')
                    : <span className="hidden sm:inline">Scarica</span>}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 w-11 h-11 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-700"
              data-testid="close-video-modal-btn"
              aria-label="Chiudi video"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="aspect-video bg-black relative select-none" style={NO_INTERACTION_STYLE}>
          {isDirectVideo && sourceUrl ? (
            <video
              key={`${content.id}:${reloadNonce}`}
              src={sourceUrl}
              controls
              controlsList={allowDownload ? 'noremoteplayback noplaybackrate' : 'nodownload noremoteplayback noplaybackrate'}
              disablePictureInPicture
              disableRemotePlayback
              playsInline
              autoPlay
              preload="metadata"
              className="w-full h-full"
              style={NO_INTERACTION_STYLE}
              data-testid="video-html5"
              onContextMenu={swallowEvent}
              onDragStart={swallowEvent}
              onLoadedMetadata={() => {
                debug('modal:videoMetadata', { id: content.id });
                setMediaReady(true);
                setLoadTimedOut(false);
              }}
              onCanPlay={() => debug('modal:videoCanPlay', { id: content.id })}
              onEnded={() => {
                debug('modal:videoEnded', { id: content.id });
                tryAutoCompleteVideoOnEnded(content.id);
              }}
              onError={(e) => {
                const err = e?.currentTarget?.error;
                debug('modal:videoError', {
                  id: content.id,
                  code: err?.code || null,
                  msg: err?.message || null,
                });
                setMediaReady(false);
                setMediaError(true);
                setLoadTimedOut(false);
              }}
            />
          ) : embedUrl ? (
            <iframe
              key={`${content.id}:${reloadNonce}`}
              src={embedUrl}
              title={content.title}
              className="w-full h-full border-0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid="video-iframe"
              onLoad={() => {
                debug('modal:iframeLoad', { id: content.id });
                setMediaReady(true);
                setLoadTimedOut(false);
              }}
              onError={() => debug('modal:iframeError', { id: content.id })}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-400 text-sm p-4 text-center">
              {sourceUrl ? 'URL video non supportato per l’anteprima in app.' : 'URL video non valido'}
            </div>
          )}

          {(isDirectVideo || embedUrl) && !mediaReady && (
            <div className="absolute inset-0 bg-black flex flex-col items-center justify-center gap-3 text-white/80 text-sm pointer-events-none">
              {mediaError || loadTimedOut ? (
                <div className="flex flex-col items-center gap-3 pointer-events-auto">
                  <p>{mediaError ? 'Impossibile riprodurre il video.' : 'Caricamento più lento del previsto.'}</p>
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="px-4 py-2 rounded-full bg-white text-black text-sm font-medium"
                    data-testid="video-retry-btn"
                  >
                    Riprova
                  </button>
                </div>
              ) : (
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
              )}
            </div>
          )}

          {/* Watermark con identificativo utente. Deterrente contro lo screen
              recording + ridistribuzione: chi cattura il video ha la propria
              email/nome stampato sopra. Pointer-events-none così non blocca i
              controlli nativi del player. */}
          {isDirectVideo && watermarkLabel && (
            <div
              className="absolute bottom-1 right-2 text-white/20 text-[10px] font-medium pointer-events-none select-none tracking-wide drop-shadow"
              style={{ textShadow: '0 0 6px rgba(0,0,0,0.7)' }}
              aria-hidden
              data-testid="video-watermark"
            >
              {watermarkLabel}
            </div>
          )}
        </div>

        {content.description && (
          <div className="p-4 border-t border-zinc-200 max-h-28 overflow-y-auto rounded-b-2xl">
            <p className="text-zinc-700 text-sm whitespace-pre-wrap">{content.description}</p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default VideoModal;
